import styled from '@emotion/styled';
import {
  Button,
  Modal,
  Rating,
  Typography,
} from '@innovamat/glimmer-components';
import type { TFunction } from 'i18next';
import type { ReactNode } from 'react';
import { useEffect, useRef, useState } from 'react';
import { Trans } from 'react-i18next';
import {
  AfterReviewContent,
  BeforeReviewContent,
  Divider,
  Footer,
  Optional,
  QuestionTextWrapper,
  QuestionWrapper,
  TextArea,
} from './session-done-modal.styles';

type UnlockModalProps = {
  applets: ReactNode;
  showDigitalPracticeInformation?: boolean;
  hasAlreadyValue: boolean;
  isOnlyRating: boolean;
  isOpen: boolean;
  isSendReviewPending: boolean;
  ratingValue: number | null;
  review: string;
  solutions: ReactNode;
  handleErrorReview: (value: string) => void;
  handleRating: (value: number | null) => void;
  handleResetRating: () => void;
  handleReview: (value: string) => void;
  handleSendReview: () => void;
  onClose: () => void;
  setIsModalClosing?: (value: boolean) => void;
  t: TFunction<'translation', undefined, 'translation'>;
};

const Content = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
`;

const GapContent = styled(Content)`
  gap: 16px;
`;

const ReviewWrapper = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
`;

export function UnlockModal({
  applets,
  showDigitalPracticeInformation,
  hasAlreadyValue,
  isOnlyRating,
  isOpen,
  isSendReviewPending,
  ratingValue,
  review,
  solutions,
  handleErrorReview,
  handleRating,
  handleResetRating,
  handleReview,
  handleSendReview,
  onClose,
  setIsModalClosing,
  t,
}: UnlockModalProps): JSX.Element {
  const beforeRef = useRef<HTMLDivElement>(null);
  const afterRef = useRef<HTMLDivElement>(null);
  const [beforeHeight, setBeforeHeight] = useState<number | undefined>();
  const [afterHeight, setAfterHeight] = useState<number | undefined>();
  const [showErrorInput, setShowErrorInput] = useState(false);
  const [isModified, setIsModified] = useState(false);

  const getShowReviewZone = (): boolean => {
    if (isModified) return true;
    if (isOnlyRating) return true;
    if (hasAlreadyValue) return false;
    return false;
  };

  const getShowAppletsZone = (): boolean => {
    if (isOnlyRating) return false;
    if (isModified) return false;
    return true;
  };

  const showReviewContent = getShowReviewZone();
  const showAppletsContent = getShowAppletsZone();

  const handleGoBack = (): void => {
    setIsModified(false);
    handleResetRating();
  };

  const handleOnClose = (): void => {
    showDigitalPracticeInformation ? setIsModalClosing?.(true) : onClose();
  };

  useEffect(() => {
    if (!isOpen) {
      setShowErrorInput(false);
      setIsModified(false);
      return;
    }

    if (afterRef.current) {
      setAfterHeight(afterRef.current.scrollHeight);
    }

    if (beforeRef.current) {
      setBeforeHeight(beforeRef.current.scrollHeight);
    }
  }, [isOpen, showErrorInput]);

  return (
    <Modal
      closeOnEsc
      isOpen={isOpen}
      modalWidth="768px"
      onClose={handleOnClose}
      overlayTestId="blockLayoutModal"
      title={
        isOnlyRating
          ? t('session.sesion-review.modal.title')
          : t('session.unlockModal.unlockContent.title')
      }
      closeButton="inner"
    >
      <Content>
        {!isOnlyRating && (
          <BeforeReviewContent
            ref={beforeRef}
            height={beforeHeight!}
            isVisible={showAppletsContent}
          >
            {solutions}
            {applets}
          </BeforeReviewContent>
        )}

        <ReviewWrapper>
          <Typography.Subtitle1>
            {t('session.sesion-review.modal.subtitle')}
          </Typography.Subtitle1>
          <Rating
            onChange={(e, value) => {
              setIsModified(true);
              handleRating(value);
            }}
            value={ratingValue}
          />
        </ReviewWrapper>
        <AfterReviewContent
          ref={afterRef}
          isVisible={showReviewContent}
          height={afterHeight!}
        >
          <GapContent>
            <Divider margin="top" />

            <QuestionWrapper>
              <QuestionTextWrapper>
                <Typography.Subtitle1>
                  <Trans
                    i18nKey="session.sesion-review.modal.error-title"
                    components={{
                      small: <Optional />,
                    }}
                  />
                </Typography.Subtitle1>
                <Optional>
                  {t('session.sesion-review.modal.error-description')}
                </Optional>
              </QuestionTextWrapper>
              {showErrorInput ? (
                <TextArea
                  placeholder={t(
                    'session.sesion-review.modal.report-error.plahceholder'
                  )}
                  rows={2}
                  onChange={(e) => handleErrorReview(e.target.value)}
                ></TextArea>
              ) : (
                <Button
                  variant="secondary"
                  onClick={() => setShowErrorInput(true)}
                >
                  {t('session.sesion-review.modal.report-error.button')}
                </Button>
              )}
            </QuestionWrapper>

            <Typography.Subtitle1>
              <Trans
                i18nKey="session.sesion-review.modal.message.content"
                components={{
                  small: <Optional />,
                }}
              />
            </Typography.Subtitle1>
            <TextArea
              placeholder={t('session.sesion-review.modal.message-placeholder')}
              rows={2}
              onChange={(e) => handleReview(e.target.value)}
              value={review}
            ></TextArea>
            <Footer>
              {!isOnlyRating && (
                <Button onClick={handleGoBack} variant="tertiary">
                  {t('back.button')}
                </Button>
              )}

              <Button
                loading={isSendReviewPending}
                onClick={handleSendReview}
                disabled={ratingValue === null}
              >
                {t('session.sesion-review.modal.send')}
              </Button>
            </Footer>
          </GapContent>
        </AfterReviewContent>
      </Content>
    </Modal>
  );
}
